// Kill Sheet — canonical Drill Pipe geometry resolver.
//
// ROOT-CAUSE CORRECTION (Sep 2026, support ticket 255586).
//
// The legacy Kill Sheet auto-populate (`autoDerive.js`) read Drill Pipe OD/ID
// from ONE place only: a "Drill Pipe" component inside the bound legacy BHA
// profile. The canonical Engineering Setup — the active Rig Engineering
// Session and the Drill String it binds — was never consulted. When the BHA
// carried no Drill Pipe row, the three DP capacities (DP internal, DP/casing
// annular, DP/HWDP open-hole annular) stayed blank, and the operator had to
// duplicate Drill Pipe data into the BHA purely so the sheet could compute.
//
// This module is the single read path for Drill Pipe geometry. Precedence
// (highest first) — confirmed with the operator:
//
//   1. Active Rig Engineering Session → its bound Drill String → the
//      `drill_pipe` section's outerDiameter / innerDiameter.
//   2. The session's well → `well.activeDrillStringId` → same section lookup
//      (mirrors `_effectiveDrillStringId` in lib/rce/derivers.js).
//   3. Legacy BHA profile "Drill Pipe" component OD/ID — compatibility
//      fallback only (today's behaviour, unchanged).
//   4. Nothing resolvable → nulls; the cells render blank exactly as before.
//
// Manual overrides are NOT handled here — `deriveSession` applies them after
// derivation, so a user-entered value always wins over every source below.
//
// Storage readers are injectable via `deps` so the resolver (and
// `deriveSession`) can be unit-tested in Node without a DOM. Never throws.

import { getEngineeringSession } from "@/lib/rce/session";
import { getWellProfile } from "@/lib/well/storage";
import { getDrillString } from "@/lib/drillstring/storage";

export const DP_GEOMETRY_SOURCE = Object.freeze({
  SESSION_DRILL_STRING: "drillstring:session",
  WELL_DRILL_STRING:    "drillstring:well",
  BHA_COMPONENT:        "bha",
});

const IN_TO_MM = 25.4;

const _num = (v) => {
  if (v && typeof v === "object" && "value" in v) v = v.value;
  if (v === "" || v == null) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};
const _isNonEmpty = (v) => v !== "" && v !== null && v !== undefined;

// Effective section length — same rule as lib/drillstring/schema.js
// (explicit lengthFt wins, else jointCount × nominalJointLengthFt).
const _sectionEffLen = (s) => {
  const len = _num(s.lengthFt);
  if (len != null) return len;
  const jc = _num(s.jointCount);
  const nl = _num(s.nominalJointLengthFt);
  return (jc != null && nl != null) ? jc * nl : null;
};

function _emptyGeometry(wellId = "") {
  return {
    source: null,
    odIn: null,                 // canonical sources: inches. BHA fallback: as entered.
    idIn: null,
    unitsAsEntered: null,       // set only for the BHA fallback
    drillStringId: "",
    drillStringName: "",
    drillStringRevision: "",
    sectionUid: "",
    sectionLabel: "",
    wellId,
  };
}

/**
 * Pick the `drill_pipe` section that represents "Drill Pipe" on a
 * single-DP-line IWCF sheet. Only sections with a valid OD > ID > 0 qualify.
 * When several qualify (tapered string), the longest section wins because
 * it dominates the string volume; when lengths are unknown, the first
 * section walking bit-end upward is used. Returns null when none qualify.
 */
export function pickDrillPipeSection(ds) {
  if (!ds || !Array.isArray(ds.sections)) return null;
  const candidates = ds.sections.filter((s) => {
    if (!s || s.role !== "drill_pipe") return false;
    const od = _num(s.outerDiameter);
    const id = _num(s.innerDiameter);
    return od != null && id != null && od > 0 && id > 0 && od > id;
  });
  if (candidates.length === 0) return null;
  let best = candidates[0];
  let bestLen = _sectionEffLen(best);
  for (const s of candidates.slice(1)) {
    const L = _sectionEffLen(s);
    if (L != null && (bestLen == null || L > bestLen)) { best = s; bestLen = L; }
  }
  return best;
}

function _fromDrillString(ds, source, wellId) {
  const sec = pickDrillPipeSection(ds);
  if (!sec) return null;
  return {
    ..._emptyGeometry(wellId),
    source,
    odIn: _num(sec.outerDiameter),
    idIn: _num(sec.innerDiameter),
    drillStringId:       ds.id || "",
    drillStringName:     ds.name || ds.stringNumber || "",
    drillStringRevision: ds.revision || "",
    sectionUid:          sec.sectionUid || "",
    sectionLabel:        sec.displayName || "Drill Pipe",
  };
}

function _bhaDrillPipeComponent(bha) {
  if (!bha || !Array.isArray(bha.components)) return null;
  return bha.components.find((c) => /drill\s*pipe|^dp$/i.test((c && c.kind) || "")) || null;
}

/**
 * resolveDrillPipeGeometry(session, deps?) → geometry record (see
 * `_emptyGeometry` for the shape). `session` is the legacy Kill Sheet
 * session (needed only for the BHA fallback). `deps` may override the
 * storage readers: { getEngineeringSession, getWellProfile, getDrillString }.
 */
export function resolveDrillPipeGeometry(session, deps = {}) {
  const readSession = deps.getEngineeringSession || getEngineeringSession;
  const readWell    = deps.getWellProfile        || getWellProfile;
  const readDs      = deps.getDrillString        || getDrillString;

  let eng = null;
  try { eng = readSession(); } catch { eng = null; }
  const wellId = (eng && eng.wellId) || "";
  const sessionDsId = (eng && _isNonEmpty(eng.drillStringId)) ? eng.drillStringId : "";

  // 1. Active Rig Engineering Session → bound Drill String.
  if (sessionDsId) {
    try {
      const ds = readDs(sessionDsId);
      const r = ds ? _fromDrillString(ds, DP_GEOMETRY_SOURCE.SESSION_DRILL_STRING, wellId) : null;
      if (r) return r;
    } catch { /* fall through */ }
  }

  // 2. Session well → its active Drill String.
  if (wellId) {
    try {
      const w = readWell(wellId);
      const activeId = (w && _isNonEmpty(w.activeDrillStringId)) ? w.activeDrillStringId : "";
      if (activeId && activeId !== sessionDsId) {
        const ds = readDs(activeId);
        const r = ds ? _fromDrillString(ds, DP_GEOMETRY_SOURCE.WELL_DRILL_STRING, wellId) : null;
        if (r) return r;
      }
    } catch { /* fall through */ }
  }

  // 3. Legacy BHA "Drill Pipe" component — compatibility fallback. Values are
  //    taken exactly as entered (the pre-correction behaviour), including the
  //    case where only OD or only ID is present.
  const comp = _bhaDrillPipeComponent(session && session.bha);
  if (comp) {
    const od = _num(comp.odIn);
    const id = _num(comp.idIn);
    if (od != null || id != null) {
      return {
        ..._emptyGeometry(wellId),
        source: DP_GEOMETRY_SOURCE.BHA_COMPONENT,
        odIn: od,
        idIn: id,
        unitsAsEntered: (session && session.bha && session.bha.units) || (session && session.units) || "IMP",
        sectionLabel: comp.kind || "Drill Pipe",
      };
    }
  }

  // 4. Nothing.
  return _emptyGeometry(wellId);
}

/**
 * Express a geometry record's OD/ID in the Kill Sheet session's unit system:
 * inches for "IMP", millimetres for "SI". Canonical Drill String values are
 * always stored in inches (lib/drillstring/schema.js), so they are converted
 * for SI sheets. BHA-fallback values are used as entered — exactly what the
 * capacity maths received before this correction.
 */
export function drillPipeDimsForUnits(geom, units) {
  if (!geom) return { od: null, id: null };
  const od = _num(geom.odIn);
  const id = _num(geom.idIn);
  const canonical = geom.source === DP_GEOMETRY_SOURCE.SESSION_DRILL_STRING
                 || geom.source === DP_GEOMETRY_SOURCE.WELL_DRILL_STRING;
  if (canonical && units === "SI") {
    return { od: od != null ? od * IN_TO_MM : null, id: id != null ? id * IN_TO_MM : null };
  }
  return { od, id };
}

/** Short human-readable provenance for the UI. */
export function describeDrillPipeSource(geom) {
  if (!geom || !geom.source) return "No Drill Pipe geometry found.";
  const name = geom.drillStringName ? `“${geom.drillStringName}”` : "Drill String";
  const rev  = geom.drillStringRevision ? ` rev ${geom.drillStringRevision}` : "";
  switch (geom.source) {
    case DP_GEOMETRY_SOURCE.SESSION_DRILL_STRING:
      return `${name}${rev} · bound to the active Rig Engineering Session`;
    case DP_GEOMETRY_SOURCE.WELL_DRILL_STRING:
      return `${name}${rev} · the well's active Drill String`;
    case DP_GEOMETRY_SOURCE.BHA_COMPONENT:
      return "Drill Pipe component in the bound BHA profile (compatibility fallback)";
    default:
      return String(geom.source);
  }
}
